(ns machtfit.product-form.promotions
  (:require [clojure.string :as str]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.materialize-widgets :as materialize-widgets]
            [machtfit.product-form.core :as product-form]
            [machtfit.product-form.handlers :as handlers]
            [machtfit.product-form.how-much :as how-much]
            [machtfit.product-form.promotion-image :as promotion-image]
            [machtfit.product-form.section :refer [show-next-section]]
            [machtfit.translation :refer [t]]
            [machtfit.utils :refer [save-scroll-position]]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::promotions
  :<- [:get [:product :promotions]]

  (fn [product-promotions _]
    (->> product-promotions
         (map-indexed vector)
         (map first))))

(rf/reg-event-fx ::add-promotion
  (fn [{:keys [db]} _]
    (let [path [:product :promotions]
          new-promotion {:open? true
                         :show-countdown? true
                         :can-delete? true}]
      {:dispatch [::handlers/do-scroll-down]
       :db (-> db
               (update-in path #(conj (or % []) new-promotion))
               save-scroll-position)})))

(rf/reg-sub ::promotion-by-index
  :<- [:get [:product :promotions]]

  (fn [product-promotions [_ n]]
    (nth product-promotions n)))

(rf/reg-sub ::description-too-big?
  :<- [:get [:product :promotions]]
  ;; by index: a new promotion has no id yet
  (fn [promotions [_ promotion-index]]
    (product-form/rich-text-too-big? (get-in promotions [promotion-index :description]))))

(defn- additional-description [promotion-index {:keys [description finished?]}]
  [:div {:style (when-not finished?
                  {:padding-top "2rem" :padding-bottom "1rem"})}
   [:div.input-field [:label.active {:style {:padding-bottom "0.4rem"}}
                      "Zusätzliche Beschreibung"]]
   (if finished?
     [:div {:dangerouslySetInnerHTML (r/unsafe-html description)}]
     [widgets/quill {:value description
                     :on-change #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :description %])}])
   (when (and (not finished?) @(rf/subscribe [::description-too-big? promotion-index]))
     [:span>i {:style {:color "red"}}
      "Der Beschreibungstext ist zu groß. Benutzen Sie ggfs. besser komprimierte, weniger oder kleinere Bilder.
       Alle Bilder zusammen sollten nicht größer als ungefähr 750 KB sein."])])

(rf/reg-sub ::promotion-labels
  :<- [:get [:product :promotions]]
  :<- [:get [:product :what :labels]]
  (fn [[promotions product-labels] [_ promotion-index]]
    (or (get-in promotions [promotion-index :labels])
        product-labels)))

(defn- promotion-labels [promotion-index]
  (let [open? (r/atom false)]
    (fn []
      [:div.valign-wrapper
       [:div.col.s12
        [:p @open?]
        [:p "Labels zuweisen" [:a.waves-effect
                               {:on-click #(swap! open? not)
                                :style {:margin-left "30px"}}
                               [:i.material-icons.tiny (if @open? "expand_less" "expand_more")]
                               (if @open? "Zuklappen" "Aufklappen")]]
        (when @open?
          (doall
           (for [[group labels'] @(rf/subscribe [:get [:labels-with-groups]])]
             ^{:key group}
             [:div
              [:span {:style {:font-weight "bold"}}
               (if (= group :null) "Nicht zugeordnet" group)]
              (materialize-widgets/multi-select {:name "labels"
                                                 :values @(rf/subscribe [::promotion-labels promotion-index])
                                                 :choices (sort labels')
                                                 :on-change #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :labels %])})])))]])))

(defn promotion-campaign-title [promotion-index {title :name :keys [id finished?]}]
  [widgets/block-input
   {:id (str "promotion-name-" id)
    :disabled finished?
    :required? true
    :label-text "Aktionsname"
    :value title
    :on-change #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :name %])
    :block-style {:width "100%"}
    :style {:width "100%"}}])

(defn promotion-start-date [promotion-index {:keys [start-date finished? active?]}]
  [:span "Startdatum: "
   [widgets/inline-date-input
    {:read-only? (or finished? active?)
     :required? true
     :value start-date
     :on-save #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :start-date %])}]])

(defn promotion-end-date [promotion-index {:keys [end-date finished?]}]
  [:span "Enddatum: "
   [widgets/inline-date-input
    {:read-only? finished?
     :required? true
     :value end-date
     :on-save #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :end-date %])}]])

(defn promotion-product-title [promotion-index {:keys [id title finished?]}]
  [:div.valign-wrapper
   [widgets/block-input
    {:id (str id "-title")
     :disabled finished?
     :label-text "Alternativer Produkttitel"
     :value (or title @(rf/subscribe [:get [:product :what :title]]))
     :on-change #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :title %])
     :block-style {:width "100%"}
     :style {:width "100%"}}]])

(defn promotion-border-color [promotion-index {:keys [border-color finished?]}]
  [:div.valign-wrapper
   [widgets/color-picker {:value (or border-color "#34B78F")
                          :read-only? finished?
                          :label-text "Rahmenfarbe"
                          :input-style {:font-size "2rem" :text-align "right" :margin-right "1rem"}
                          :on-change #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :border-color (-> % .-target .-value)])}]])

(defn promotion-countdown [promotion-index {:keys [id show-countdown? finished?]}]
  [:div.valign-wrapper
   [:input.filled-in
    {:type "checkbox"
     :disabled finished?
     :id (str id "-show-countdown?")
     :checked show-countdown?
     :on-change #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :show-countdown? (.. % -target -checked)])}]
   [:label.black-text {:for (str id "-show-countdown?")
                       :style {:height "auto"
                               :line-height "normal"}}
    "Teilnehmer sehen 48 Stunden vor Ende der Aktion einen Countdown"]])

(defn promotion-access-codes [promotion-index {:keys [id has-access-code? access-codes finished?]}]
  [:div.valign-wrapper
   {:style {:flex-direction "column"
            :align-items "flex-start"}}
   [:input.filled-in
    {:type "checkbox"
     :disabled finished?
     :id (str id "-promotion-has-access-code")
     :checked has-access-code?
     :on-change #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :has-access-code? (.. % -target -checked)])}]
   [:label.black-text {:for (str id "-promotion-has-access-code")
                       :style {:height "auto"
                               :line-height "normal"}}
    "Teilnehmer erhalten persönlichen Zugangscode"]
   (when has-access-code?
     [widgets/textarea {:id (str id "-promotion-access-codes")
                        :read-only? finished?
                        :label-text [:span "Zugangscodes "
                                     [widgets/tooltip {:icon "icon-question-sign"
                                                       :content "Ein Zugangscode pro Zeile "}]]
                        :value access-codes
                        :cleaner widgets/clean-list-of-strings
                        :on-save #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :access-codes %])}])])

(defn promotion-amount [promotion-index {:keys [id price finished?]}]
  [:div.row {:style {:margin-left "0.2rem"}}
   [:div.col {:style {:margin-top "1rem"}}
    [widgets/amount-input {:id (str id "-promotion-amount")
                           :read-only? finished?
                           :label-text "Preis"
                           :value (or price @(rf/subscribe [:get [:product :how-much :amount]]))
                           :input-style {:font-size "2rem" :text-align "right"}
                           :handler #(rf/dispatch [::how-much/handle-amount [:product :promotions promotion-index :price] %])}]]
   [:div.col {:style {:margin-top "1rem"
                      :padding-left 0}}
    [:div.input-field
     [:span {:style {:font-size "2rem"}} "€"]]]])

(defn promotion-commission [promotion-index {:keys [commission-rate finished?]}]
  [:div.valign-wrapper
   [widgets/input-with-label
    {:read-only? finished?
     :value (or commission-rate @(rf/subscribe [:get [:product :how-much :commission]]))
     :label-text "Provision"
     :type widgets/type-percent-decimal
     :on-save #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :commission-rate %])
     :style {:width 50 :text-align "center"}}]])

(defn promotion-editor [promotion-index {:keys [image-id active?] :as promotion}]
  [:div {:style {:margin-left "-8.5%"}}
   (when @(rf/subscribe [:get [:ui :promotion-image-media-library-active?]])
     [promotion-image/image-library promotion-index image-id])

   [:div.col.s12
    [promotion-campaign-title promotion-index promotion]
    [:div.promotion-inline-content
     [promotion-start-date promotion-index promotion]
     [promotion-end-date promotion-index promotion]
     (when active?
       [:div.valign-wrapper
        [:img {:src "/static/img/activity/checkmark.svg"}]
        [:span (t {:de "Aktiv"
                   :en "Active"})]])]
    [promotion-product-title promotion-index promotion]
    [additional-description promotion-index promotion]
    [promotion-border-color promotion-index promotion]
    [promotion-countdown promotion-index promotion]
    [promotion-image/image promotion-index {:image-id image-id} true]
    [promotion-labels promotion-index]
    (when @(rf/subscribe [:get [:product :how :needs-access-code?]])
      [promotion-access-codes promotion-index promotion])
    [:div.promotion-inline-content
     [promotion-amount promotion-index promotion]
     [promotion-commission promotion-index promotion]]]])

(defn promotion-detail [promotion-index promotion]
  (let [{:keys [image-id title description border-color show-countdown? labels has-access-code? access-codes price commission-rate]} promotion]
    [:<>
     [promotion-campaign-title promotion-index promotion]
     [:div.promotion-inline-content
      [promotion-start-date promotion-index promotion]
      [promotion-end-date promotion-index promotion]]
     (when (seq title)
       [promotion-product-title promotion-index promotion])
     (when (seq description)
       [additional-description promotion-index promotion])
     (when (seq border-color)
       [promotion-border-color promotion-index promotion])
     (when show-countdown?
       [promotion-countdown promotion-index promotion])
     (when image-id
       [promotion-image/image promotion-index promotion true])
     (when (seq labels)
       [promotion-labels promotion-index])
     (when (and has-access-code? (seq access-codes))
       [promotion-access-codes promotion-index promotion])
     [:div.promotion-inline-content
      (when price
        [promotion-amount promotion-index promotion])
      (when commission-rate
        [promotion-commission promotion-index promotion])]]))

(defn- promotion-c [promotion-index]
  (let [path [:product :promotions promotion-index]
        {promotion-title :name
         :keys [start-date
                end-date
                active?
                finished?
                open?
                deleted?
                can-delete?]
         :as promotion} @(rf/subscribe [::promotion-by-index promotion-index])]
    [:div.row.promotion (when deleted? {:style {:text-decoration "line-through"}})
     [:div.col.promotion-info
      [:span
       [:strong (if (str/blank? promotion-title)
                  "neue Promotion" promotion-title)]]
      [:span [:strong start-date] " - " [:strong end-date]]]
     [:div.promotion-status
      (cond finished?
            [:span {:style {:border-color "grey"}} "abgelaufen"]

            active?
            [:span {:style {:border-color "green"}} "aktiv"]

            :else
            [:span {:style {:border-color "red"}} "inaktiv"])]
     [:div.promotion-actions
      (when-not deleted?
        [:div.col.s2.promotion-accordion
         [:a.waves-effect
          {:on-click #(rf/dispatch [:toggle (conj path :open?)])}
          [:i.material-icons.tiny (if open? "expand_less" "expand_more")]
          (if open? "Zuklappen" "Aufklappen")]])

      [:div.col.s4.promotion-delete-btn-container
       {:style {:margin-bottom "0.5rem"}
        :class (when deleted? "offset-s2")}
       [:button.waves-effect.btn.btn-flat
        (-> {:style {:margin-left "0.5rem"
                     :padding "0 0.8rem"}}
            (into (if can-delete?
                    {:on-click #(rf/dispatch [:toggle (conj path :deleted?)])}
                    {:class "disabled"
                     :title "Die Aktion kann nicht gelöscht werden, da sie derzeit aktiv ist oder in der Vergangenheit liegt."})))
        [:i.material-icons.left (if deleted?
                                  "settings_backup_restore"
                                  "delete")]
        (if deleted?
          "Wiederherstellen"
          "Löschen")]]

      (when (and open? (not deleted?))
        [:div.promotion-content
         ^{:key "first-promotion"}
         (if finished?
           (promotion-detail promotion-index promotion)
           (promotion-editor promotion-index promotion))])]]))

(defn- add-promotion-button []
  ^{:key "add-promotion-button"}
  [:div.col.s12 {:style {:margin-bottom "1rem" :margin-top "1rem"}}
   [:button.waves-effect.waves-light.btn
    {:on-click #(rf/dispatch [::add-promotion])}
    [:i.material-icons.left "add"] "Aktion hinzufügen"]])

(defn all-promotions []
  (let [promotions @(rf/subscribe [::promotions])]
    [:div
     (list
      ^{:key "promotions"}
      (for [promotion-index promotions]
        ^{:key (str "promotion-" promotion-index)}
        [promotion-c promotion-index])
      ^{:key "add-promotion"} [add-promotion-button])]))

(defn section-promotions []
  [widgets/section
   {:anchor "promotions"
    :title "Aktionen"
    :content
    [:div
     [all-promotions]
     [show-next-section :promotions]]}])
